import React from 'react';

import styled from '@emotion/styled';

import { ExternalContent, mq } from '@clutter/clean';

import {
  DESKTOP_HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
} from '@shared/header/header';

const Content = styled(ExternalContent)`
  p {
    ${mq({
      scrollMarginTop: [MOBILE_HEADER_HEIGHT, null, DESKTOP_HEADER_HEIGHT],
    })}
  }

  ol ol {
    list-style: upper-alpha;
    margin-top: 16px;
  }
`;

export const TermsOfUse = () => (
  <Content>
    <p style={{ textAlign: 'center' }}>
      <strong>CLUTTER</strong>
    </p>
    <p style={{ textAlign: 'center' }}>
      <strong>TERMS OF USE</strong>
    </p>
    <p>
      Welcome to Clutter! These Terms of Use (&ldquo;<strong>Terms</strong>
      &rdquo;) of Clutter Inc. and Clutter RE LLC (together, &ldquo;
      <strong>Clutter</strong>,&rdquo; &ldquo;
      <strong>we</strong>,&rdquo; or &ldquo;<strong>us</strong>&rdquo;) are an
      agreement that describes your rights and responsibilities as a Clutter
      User, as defined below. This page explains the terms by which you may
      access or use our online and/or mobile services, web sites, applications,
      software, and documentation provided on or in connection with clutter.com
      and any services provided by us (collectively the &ldquo;
      <strong>Site</strong>
      &rdquo;), including our smart storage, moving, and related services, as
      further described and defined below (collectively the &ldquo;
      <strong>Service(s)</strong>&rdquo;). The Services include the Moving
      Services and the Smart Storage Services, as defined below, plus any other
      Services that Clutter may offer from time to time, in its sole discretion.
      These Terms are a legally binding agreement that describe your legal
      rights, remedies, and obligations and they also describe Clutter&rsquo;s
      commitments, legal rights, and remedies, so please read them carefully.
    </p>
    <p>
      By accessing or using the Site and/or using the Services (including
      reserving or booking the Services with Clutter, online, via phone, or
      otherwise, even if such Services are later canceled), clicking a button or
      checking a box marked &ldquo;I Agree&rdquo; or similar, or accessing your
      Clutter Account, you signify that you have read, understood, and agree to
      be bound by these Terms, to any additional terms in your Clutter Account,
      and to the collection, use, and disclosure of your information as set
      forth in the Clutter{' '}
      <a
        href="https://www.clutter.com/legal/privacy_policy"
        target="_blank"
        rel="noopener"
      >
        <span>Privacy Policy</span>
      </a>
      , which is hereby incorporated by reference. These Terms apply to all
      visitors, users, and others who register for or otherwise access the Site
      or use the Services (&ldquo;<strong>Users</strong>&rdquo;, &ldquo;
      <strong>you</strong>&rdquo;, or &ldquo;<strong>your</strong>&rdquo;).
    </p>
    <ol>
      <li>
        <div>
          <strong>USE OF OUR SITE</strong>
        </div>
        <ol>
          <li>
            <div>Eligibility</div>
            <p>
              This is a contract between you and Clutter. You must read and
              agree to these Terms before using the Site or Service. If you do
              not agree to these Terms, you may not use the Service. You may use
              the Site or Service only if you can form a binding contract with
              Clutter, and only in compliance with these Terms and all
              applicable local, state, national, and international laws, rules,
              and regulations. You must be at least 18 years old to use the Site
              and Services. By using or accessing the Site and Services, you
              represent and warrant that you are 18 years of age or older. Any
              use or access of the Site or Services by anyone under 18 is
              strictly prohibited and in violation of these Terms. The Site and
              Services may not be available to any Users previously removed from
              the Site by Clutter.
            </p>
          </li>
          <li>
            <div>Clutter Site</div>
            <p>
              You are hereby granted a non-exclusive, limited, non-transferable,
              freely revocable license to use the Site for your personal,
              noncommercial use only and as permitted by the features of the
              Site. Clutter may terminate this license at any time for any
              reason or no reason. Clutter reserves all rights not expressly
              granted herein in the Site and the Clutter Content (as defined
              below).
            </p>
          </li>
          <li>
            <div>Clutter Accounts</div>
            <p>
              Your account on the Site (your &ldquo;
              <strong>Clutter Account</strong>
              &rdquo;) gives you access to the Services and other functionality
              that we may establish and maintain from time to time, in our sole
              discretion. We may maintain different types of Clutter Accounts
              for different types of Users or different types of Services. If
              you open a Clutter Account on behalf of a company, organization,
              or other entity, then: (a) &ldquo;you&rdquo; includes you and that
              entity, and (b) you represent and warrant that you are an
              authorized representative of the entity with the authority to bind
              the entity to these Terms, and that you agree to these Terms on
              the entity&rsquo;s behalf. By connecting to Clutter with a
              third-party service, you give us permission to access and use your
              information from that service as permitted by that service, and to
              store your log-in credentials for that service.
            </p>
            <p>
              You may never use another User&rsquo;s Clutter Account without
              permission. When creating your Clutter Account, you must provide
              accurate and complete information, and you must keep this
              information up to date. You are solely responsible for the
              activity that occurs on your Clutter Account, and you must keep
              your account password secure. We encourage you to use strong
              passwords (passwords that use a combination of upper and
              lower-case letters, numbers, and symbols) with your Clutter
              Account. You must notify Clutter immediately of any breach of
              security or unauthorized use of your Clutter Account. Clutter will
              not be liable for any losses caused by any unauthorized use of
              your Clutter Account.
            </p>
            <p>
              You may control your User profile and account settings by logging
              into your Clutter Account at https://account.clutter.com and
              changing the settings in your settings page. By providing Clutter
              with your email address, you consent to our using the email
              address to send you Site- and Service-related notices, including
              any notices required by law, in lieu of communication by postal
              mail, and you also consent to receiving auction notices under the
              Uniform Commercial Code (UCC) Section 7210 via email in addition
              to or in lieu of certified mail. We may also use your email
              address to send you other messages, such as changes to features of
              the Site or Services, marketing content, and special offers. If
              you do not want to receive such email messages, you may opt out or
              change your preferences in your settings page by logging into your
              Clutter Account at https://account.clutter.com. Opting out may
              prevent you from receiving email messages regarding updates,
              improvements, or offers.
            </p>
          </li>
          <li>
            <div>Site Rules</div>
            <p>
              You agree not to engage in any of the following prohibited
              activities: (i) copying, distributing, or disclosing any part of
              the Site in any medium, including without limitation by any
              automated or non-automated &ldquo;scraping&rdquo;; (ii) using any
              automated system, including without limitation
              &ldquo;robots,&rdquo; &ldquo;spiders,&rdquo; &ldquo;offline
              readers,&rdquo; etc., to access the Site in a manner that sends
              more request messages to the Clutter servers than a human can
              reasonably produce in the same period of time by using a
              conventional on-line web browser (except that Clutter grants the
              operators of public search engines revocable permission to use
              spiders to copy publicly available materials from the Site for the
              sole purpose of and solely to the extent necessary for creating
              publicly available searchable indices, but not caches or archives,
              of such materials); (iii) transmitting spam, chain letters, or
              other unsolicited email; (iv) attempting to interfere with,
              compromise the system integrity or security of, or decipher any
              transmissions to or from the servers running the Site; (v) taking
              any action that imposes, or may impose at our sole discretion, an
              unreasonable or disproportionately large load on our
              infrastructure; (vi) uploading invalid data, viruses, worms, or
              other software agents through the Site; (vii) collecting or
              harvesting any personally identifiable information, including
              account names, from the Site; (viii) using the Site for any
              commercial solicitation purposes; (ix) impersonating another
              person or otherwise misrepresenting your affiliation with a person
              or entity, conducting fraud, hiding or attempting to hide your
              identity; (x) interfering with the proper working of the Site;
              (xi) accessing any content on the Site through any technology or
              means other than those provided or authorized by the Site; or
              (xii) bypassing the measures we may use to prevent or restrict
              access to the Site, including without limitation features that
              prevent or restrict use or copying of any content or enforce
              limitations on use of the Site or the content therein.
            </p>
          </li>
          <li>
            <div>Changes to the Site or Services</div>
            <p>
              For you specifically or Users generally, we may, without prior
              notice: change the Site or Services; stop providing the Site or
              Services or features thereof; or create usage limits for the Site
              or Services. We may permanently or temporarily terminate or
              suspend your access to the Site or Services without notice and
              liability for any reason, including if in our sole determination
              you violate any provision of these Terms, or for no reason. Upon
              termination for any reason or no reason, you continue to be bound
              by these Terms.
            </p>
          </li>
          <li>
            <div>Disputes with Other Users</div>
            <p>
              You are solely responsible for your interactions with and
              obligations to other Users. We reserve the right, but have no
              obligation, to monitor disputes between you and other Users.
              Clutter shall have no liability for your interactions with or
              obligations to other Users, or for any User&rsquo;s action or
              inaction.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <div>
          <strong>THE CLUTTER SERVICES</strong>
        </div>
        <ol>
          <li>
            <div>Clutter</div>
            <p>
              Clutter shall provide certain Services, which you may select and
              schedule on clutter.com, by phone, via customer support, and/or in
              your Clutter Account, as applicable and available. Clutter&rsquo;s
              moving services consist of transporting Customer Items, as defined
              below, from a designated pickup address (or multiple pickup
              addresses, if available) that you provide to us (&ldquo;
              <strong>Pickup Address</strong>&rdquo;) to a designated delivery
              address (or multiple delivery addresses, if available) that you
              provide to us (&ldquo;
              <strong>Delivery Address</strong>&rdquo;), plus any related add-on
              services such as wrapping, packing, and boxing the Customer Items
              (together, the &ldquo;<strong>Moving Services</strong>&rdquo;).
              Clutter&rsquo;s smart storage services consist of photographing,
              inventorying, and palletizing the Customer Items, as defined
              below, picking them up from a Pickup Address, and storing them for
              an indefinite period of time in a facility owned, leased,
              subleased, licensed, or sublicensed by Clutter, plus any related
              add-on services such as wrapping, packing, or boxing the Customer
              Items, adding to or returning from storage a subset of Customer
              Items to a Delivery Address, and making a final return of the
              Customer Items from storage to a Delivery Address (together, the
              &ldquo;
              <strong>Smart Storage Services</strong>&rdquo;). From time to time
              and subject to additional fees, Clutter may offer other additional
              Services.
            </p>
            <p>
              &ldquo;<strong>Customer Items</strong>&rdquo; means the items
              packed by you, Clutter, a third party, and/or a third party on
              behalf of Clutter for the Smart Storage or Moving Services. You
              acknowledge and agree that, in the event that the Customer Items
              are stolen or are otherwise missing prior to pick-up of the
              Customer Items by Clutter at the Pickup Address or after drop-off
              of the Customer Items by Clutter at the Delivery Address (&ldquo;
              <strong>Lost Items</strong>&rdquo;), Clutter will not be liable
              for and hereby expressly disclaims all liability for the Lost
              Items and any damages resulting from the Lost Items.
            </p>
            <p>
              In the event your email address, residence address, mailing
              address, billing address, phone number, Pickup Address, and/or
              Delivery Address changes, you must update Clutter within fourteen
              (14) calendar days of the change by updating your information in
              your Clutter Account, if available, or by submitting a support
              request in your Clutter Account. No other notice of a change of
              address shall be valid or binding against Clutter. Changes of
              e-mail address, residence address, mailing address, billing
              address, Pickup Address, and/or Delivery Address cannot be made by
              any other method, including but not limited to by email or by
              telephone. We are not responsible if you do not receive a notice
              from us because any of your addresses changed and you did not
              update your Clutter Account. You acknowledge and agree that in the
              absence of current contact information, your last known address on
              file with Clutter will be used for all notices required to be
              provided to you by law or as required under these Terms.
            </p>
            <p>
              You represent and warrant that you are the owner of any cell
              phone, landline, voice over IP (VOIP), or mobile phone number
              contained in your Clutter Account, and that you shall remove any
              cell phone, landline, VOIP, or mobile phone number contained in
              your Clutter Account if you surrender that number or transfer it
              to any other person. You further agree that we may contact you
              using any of the information contained in your Clutter Account,
              including any listed cell phone, landline, VOIP, or mobile phone
              number.
            </p>
            <p>
              Clutter will not pick up, deliver, or return Customer Items at or
              to an address that is outside our then-current delivery zone (a
              &ldquo;
              <strong>Delivery Zone</strong>&rdquo;), unless we otherwise agree
              to do so in writing. To determine whether a zip code is within a
              Delivery Zone, you may input a zip code into clutter.com and
              review the results. Times given for pickup, delivery, collection,
              packing, and returns are only estimates and Clutter shall not be
              liable for any delays.
            </p>
            <p>
              Clutter may, in its sole discretion, refuse, cancel, postpone, or
              otherwise reschedule any Services, including pick up, delivery,
              packing, collection, or return of Customer Items, for any reason
              or no reason, including, without limitation: unsafe travel or
              weather conditions; unsafe onsite conditions; harassing, rude, or
              threatening conduct by Users, whether actual or perceived;
              limited, no, or obstructed access to the Pickup Address or
              Delivery Address; exposure to or evidence of insect, rodent, or
              mold activity or infestation; complications due to a User being
              evicted from a Pickup Address or Delivery Address; Department of
              Transportation or state tariff regulations; labor shortage;
              unexpected logistical challenges; prior or current unpaid account
              balances or incomplete documentation; or any other reason.
            </p>
            <p>
              Clutter may use subcontractors and/or other third parties to help
              perform or support the Services, including without limitation,
              pick-ups, returns, deliveries, transportation, packing, inventory
              management, palletizing, photographing, customer service,
              communications, and other logistics with respect to the Services
              or Customer Items.
            </p>
            <p>
              You acknowledge and agree that Clutter is not responsible for
              disassembling, reassembling, disconnecting, reconnecting,
              removing, or hanging the following Customer Items: furniture,
              appliances, electronics, fixtures, wall hangings, wall mountings,
              fittings, equipment, or any item that requires licensing,
              certification, or specialized tools to handle. While Clutter makes
              a good faith effort to place all Customer Items in suitable
              environments during transport or storage, Clutter transports and
              stores a large variety of items, some of which require specific
              environments or means of transport or storage. Clutter does not
              warrant that any transport vehicle or storage facility used by
              Clutter is a suitable place or means of transport or storage for
              any particular Customer Items.
            </p>
          </li>
          <li>
            <div>Electronic Contract</div>
            <p>
              You agree that your order for the Services, whether made by
              clicking a &ldquo;Confirm Appointment&rdquo; button (which is your
              electronic signature), scheduling an appointment over the phone,
              or otherwise, is your agreement to conduct transactions with us
              electronically, and is your affirmative consent to our use of an
              electronic record to provide or make available to you (whichever
              is required) any information required by any statute, regulation,
              or other rule of law to be in writing with respect to any
              transaction conducted with us. You will be responsible for any
              printing or storage of electronic records for your files and
              reference. When you place an order or make a reservation for
              Services, our confirmation of your order or reservation will
              constitute our signature. We will send our confirmation to the
              email address associated with your Clutter Account.
            </p>
            <p>
              To the maximum extent permitted by applicable law, you agree and
              consent to electronically receive and sign any documentation from
              Clutter regarding the Services, including but not limited to a
              Bill of Lading, Estimate, Warehouse Receipt, Release Agreement,
              and/or Storage Contract, via your Clutter Account, DocuSign, or
              any other similar e-signature tool. To the maximum extent
              permitted by applicable law, you expressly waive your right to
              receive any documentation from Clutter regarding the Services,
              including but not limited to a Bill of Lading, Estimate, Warehouse
              Receipt, Release Agreement, and/or Storage Contract, in the time
              period set by applicable law. You agree and consent to receive
              such documentation in any time frame permitted by applicable law.
              You also agree that no certification authority or other
              third-party verification is necessary to validate your electronic
              signature and that the lack of such certification or third-party
              verification will not in any way affect the enforceability of your
              electronic signature or these Terms.
            </p>
          </li>
          <li>
            <div>Your Responsibilities; Waiver</div>
            <p>
              You will be solely responsible for: (i) obtaining and ensuring
              that Clutter or its subcontractors have access to any parking,
              elevators, stairways, walkways, access roads, or any other
              necessary means of access as may be reasonably required to carry
              out the Services, and that such means of access are in working
              order and safe for use to carry out the Services; (ii) being
              present, or ensuring that someone authorized by you to sign
              documents and make decisions regarding the Services and/or
              Customer Items is present, during the pickup, packing, delivery,
              collection, and return of the Customer Items; (iii) providing
              Clutter with your contact details and ensuring that such contact
              details are accurate and up-to-date; (iv) ensuring that the
              Customer Items you pack have been packed appropriately, safely,
              and securely so as not to cause damage or injury or the likelihood
              of damage or injury to you, your Customer Items, Clutter&rsquo;s
              property, employees, agents, contractors, business partners, other
              goods, or other individuals, whether by spreading of dampness,
              infestation, leakage, escape of fumes or substances, or otherwise;
              (v) informing Clutter immediately of any damage to the Customer
              Items or your property that occurred during performance of the
              Services, including the Moving Services or Smart Storage Services;
              and (vi) reimbursing Clutter in full an amount equal to all
              damages, liabilities, costs, claims, and expenses that Clutter may
              incur as a result of any breach by you of these Terms.
            </p>
            <p>
              You understand that Clutter is not responsible for any Customer
              Items that have been packed, moved, transported, or wrapped by you
              or by a third party at your direction. With respect to the Smart
              Storage Services, you acknowledge that you are solely responsible
              for verifying that Clutter has photographed and/or inventoried all
              the Customer Items and that the inventory Clutter provides you in
              your Clutter Account is a true and complete inventory of the
              personal property tendered. Unless you make a written claim in
              your Clutter Account within 30 days from the Smart Storage Service
              appointment date for the correction of any errors or omissions in
              the inventory of your Customer Items, it will be assumed to be a
              full and correct list of Customer Items stored hereunder and
              Clutter shall not be liable for any piece, package, carton, or
              container not listed therein.{' '}
              <span style={{ textDecoration: 'underline' }}>
                <strong>
                  You hereby waive and release Clutter from responsibility for
                  any damage to items that were not packed, moved, transported,
                  or wrapped by Clutter and, with respect to the Smart Storage
                  Services, were not inventoried by Clutter.
                </strong>
              </span>
            </p>
          </li>
          <li>
            <div>The Customer Items</div>
            <p>
              You represent and warrant that you own the Customer Items or that
              you otherwise have the right and authority to transport, store,
              move, and/or subject the Customer Items to the Services in
              accordance with these Terms. All Customer Items are handled,
              transported, and/or stored by Clutter or its subcontractors upon
              the express representation and warranty by you or your authorized
              representative(s) that you have the requisite legal possession of
              and that no other person or entity has an interest in the Customer
              Items. In the case of an ownership dispute, determination, or
              proceeding regarding the Customer Items, you are solely
              responsible for any fees and/or payments due and owed to Clutter
              in connection with the continuation or termination of the
              Services. If Clutter, in its sole discretion, is required to
              commence an interpleader action to settle any claims of
              conflicting ownership, or is made a party to any litigation or
              dispute in connection with the breach of this section, you agree
              to pay Clutter all reasonable and necessary costs, expenses, and
              attorneys&rsquo; fees that Clutter may be liable or compelled to
              pay in connection with such litigation or dispute.
            </p>
            <p>
              For Smart Storage Services, the Customer Items will be stored in a
              facility using warehouse racking and/or floor storage, and the
              Customer Items may be palletized. We will use commercially
              reasonable efforts to maintain the temperature of the facility in
              which the Customer Items are stored between 55&deg;F and 90&deg;F.
              Other than temperature, the Customer Items will not be stored in a
              climate-controlled environment. The air flow, moisture level, and
              heat will not be regulated. We will take commercially reasonable
              steps to protect the Customer Items from the growth of mold or
              similar microorganisms. However, because the Customer Items will
              not be stored in a completely climate-controlled environment, we
              cannot guarantee that mold, dust, or similar microorganisms may
              not develop on the Customer Items. We are not liable for the
              natural growth of mold, mildew or similar microorganisms on the
              Customer Items, or for rust, depreciation, or the natural
              degradation of Customer Items. You assume the risk that mold,
              rust, or similar microorganisms could develop under these
              circumstances.
            </p>
            <p>
              The Customer Items must not include and you must not provide to
              Clutter for storage or transport any of the following in
              connection with the Services: antiques (whether or not breakable
              and fragile), perishable goods, firearms, gun powder, explosives,
              alcohol, propane tanks, paint, used tires, plants, birds, fish,
              other animals, or any other living thing, arms or ammunition, any
              item which emits fumes, smells or odors, bullion (e.g.,
              gold-silver), jewelry, currency, ivory, precious metals or stones,
              any drugs, illegal substances or goods, or goods or substances
              illegally obtained, combustible or flammable materials, liquids or
              compressed gasses, diesel, petrol, oil, gas, artificial fertilizer
              or cleaning solvents, chemicals, radioactive materials or
              biological agents, toxic waste, asbestos or other materials of a
              dangerous or harmful nature, any other toxic, flammable or
              hazardous goods, human remains, or any other items, the
              possession, usage, transport or storage of which may detrimentally
              affect any of your other Customer Items or violate in any way the
              laws of the United States or any other applicable laws, rules, or
              regulations (collectively, &ldquo;
              <strong>Prohibited Customer Items</strong>&rdquo;). You shall be
              liable for and indemnify Clutter for all loss or damage caused by
              such goods and Clutter shall not be liable or responsible for safe
              delivery or storage of such goods.Clutter will have no liability
              for loss or damage resulting to the following types of goods: (a)
              documents; (b) stamps or stamp collections; (c) securities; (d)
              specie; (e) jewelry; (f) precious metals or stones; (g) currency;
              (h) or other articles of high or unusual value.
            </p>
            <p>
              According to our sole discretion, Clutter RE LLC or any of its
              employees, subcontractors, or other authorized agents may
              regularly conduct pest control inspections of any and all Customer
              Items.
            </p>
            <p>
              Clutter or any of its employees, subcontractors, or other
              authorized agents may, at any time, in their sole discretion and
              without notice to you, open any Customer Items to inspect the
              Customer Items if Clutter: (i) believes, in its sole discretion,
              that the Customer Items may include any Prohibited Customer Items;
              (ii) is required to do so by the law enforcement, fire services,
              local authorities, or by court order; (iii) determines, in its
              sole discretion, it necessary for account or warehouse
              maintenance; or (iv) considers it necessary, in its sole
              discretion, in an emergency or to prevent injury or damage to
              persons or property. Prohibited Customer Items may be disposed of
              by Clutter, in its sole discretion, in any manner that Clutter
              deems fit. Alternatively, Clutter may refuse to store or transport
              any Customer Items, or may return to you any Customer Items, at
              your cost, at any time, if, in Clutter&rsquo;s sole discretion,
              the storage, continued storage, or transport of the Customer Items
              would represent a risk to the safety of any person, the security
              of the storage site or transport vehicle, or security of any other
              goods at the storage site or in the transport vehicle.
            </p>
          </li>
          <li>
            <div>Payment, Subscription Services, and Cancellation</div>
            <p>
              By using the Services, or other paid services or products provided
              by Clutter, you agree to our pricing and payment terms, as we may
              update them from time to time. Clutter may add new Services for
              additional fees and charges, or amend fees and charges for
              existing Services, at any time, in its sole discretion. Clutter
              may also charge certain late, rush, and cancellation fees, in its
              sole discretion. Upon any such changes to fees and charges, we
              will notify you that such changes have been made via email and/or
              your Clutter Account. We may, from time to time and in our sole
              discretion, offer certain discounts, promotional offers, or
              referral codes (&ldquo;
              <strong>Promotions</strong>&rdquo;)in connection with our
              Services. You agree that Promotions are offered subject to our
              right to manage, regulate, control, modify, revoke, and/or
              eliminate the Promotions as Clutter sees fit, in its sole
              discretion, for any reason or no reason, in any general or
              specific case, and that Clutter will have no liability to you
              based on its exercise of such right.
            </p>
            <p>
              In order to receive Services, you must provide Clutter with a
              current, valid, accepted method of payment (as updated from time
              to time, &ldquo;
              <strong>Payment Method</strong>&rdquo;). All information that you
              provide in connection with a purchase or transaction or other
              monetary transaction interaction with the Site, your Clutter
              Account, or the Services must be accurate, complete, and current.
              You agree to pay all charges incurred by users of your credit
              card, debit card, or other accepted payment method used in
              connection with a purchase or transaction or other monetary
              transaction interaction with Clutter at the prices in effect when
              such charges are incurred. You will pay any applicable taxes, if
              any, relating to any such purchases, transactions, or other
              monetary transaction interactions. In the event of insufficient
              funds or declined payments, we will make repeated attempts to
              charge the Payment Method. At our discretion, we may charge the
              Payment Method with a reasonable hold amount or non-refundable
              deposit in advance of any appointments booked through your Clutter
              Account. Any amounts not paid within five (5) days when due may be
              subject to late fees.
            </p>
            <p>
              In the event that Clutter suspends or terminates your Clutter
              Account or the Services for your breach of these Terms, you
              understand and agree that you shall receive no refund or exchange
              for any unused Services, any license or subscription fees for any
              portion of the Site or Services, any content or data associated
              with your account, or for anything else, and that Clutter may be
              unable to complete any outstanding orders or appointments until
              the suspension is lifted.
            </p>
            <p>
              Further, you acknowledge and agree that{' '}
              <span style={{ textDecoration: 'underline' }}>
                <strong>
                  the Smart Storage Services or other paid services or products
                  provided by Clutter may be made available on a recurring
                  subscription basis (&ldquo;Subscription Services&rdquo;). If
                  you enroll in any Subscription Services, you agree to a
                  recurring payment program, which will continue for the
                  agreed-upon subscription period or minimum storage term, if
                  applicable, and automatically renew for additional subsequent
                  periods unless and until you cancel the Subscription Services
                  or Clutter suspends, discontinues, or terminates them.
                </strong>
              </span>{' '}
              Clutter may offer a number of subscription plans for Subscription
              Services with different conditions and limitations. Any materially
              different pricing or payment terms will be disclosed on the Site,
              in your Clutter Account, and/or in other communications made to
              you. For Subscription Services, we will automatically charge the
              payment method associated with your Clutter Account on a recurring
              basis, depending on the subscription term you choose. For Smart
              Storage Services, day one of your billing cycle is the date you
              begin storing Customer Items with us. You acknowledge that the
              amount billed may vary due to promotional offers, changes in your
              Subscription Services plan, and/or changes in applicable taxes,
              and you authorize us to charge your payment method for the
              corresponding amounts. You further acknowledge that the amount of
              the recurring charge may change if the applicable tax rates change
              or if you are notified that there will be an increase in the
              applicable subscription fees.
            </p>
            <p>
              You hereby authorize Clutter to bill the subscription fee to your
              Payment Method, along with any applicable taxes or additional fees
              due during the billing period. You must cancel the Subscriptions
              Services prior to the end of the then-current subscription period
              in order to avoid billing of the next subscription period&rsquo;s
              subscription fees to your Payment Method.
            </p>
            <p>
              <span style={{ textDecoration: 'underline' }}>
                <strong>
                  Any change to our Subscription Services&rsquo; pricing or
                  payment terms shall become effective in the billing cycle
                  following notice of such change to you as provided in these
                  Terms. Notwithstanding the foregoing, fees for booking initial
                  appointments, additional pickups and/or deliveries, final
                  returns, or other appointments or add-on services are subject
                  to change and will be calculated on the Site, at the Pickup
                  Address on the date of your appointment, or in your Clutter
                  Account at the time of booking.
                </strong>
              </span>
            </p>
            <p>
              <span style={{ textDecoration: 'underline' }}>
                <strong>
                  You may cancel your Subscription Services at any time once the
                  minimum storage term you committed to has been met; however,
                  you will not receive a refund for the then-current
                  subscription period. If your Subscription Services are
                  canceled before the minimum storage term you committed to has
                  been met, all applicable fees (including payment for the
                  remainder of the minimum storage term and any applicable
                  termination fees, disposal fees, and/or return fees) will be
                  due and charged to you upon booking a final return of the
                  Customer Items. To cancel the Subscription Services, book a
                  final return of the Customer Items through your Clutter
                  Account at https://account.clutter.com. When you cancel the
                  Subscription Services, you cancel only future charges
                  associated with your Clutter Account.
                </strong>
              </span>{' '}
              You may notify us of your intent to cancel the Subscription
              Services by booking a final return of the Customer Items, but the
              cancellation will only become effective at the end of the
              subscription period in which we perform your final return. You
              will continue to have the same access and benefits of the Services
              for the remainder of the current subscription period.
            </p>
            <p>
              <span style={{ textDecoration: 'underline' }}>
                <strong>
                  You may cancel the Moving Services at any time by canceling
                  any scheduled appointments and paying any outstanding fees due
                  to Clutter through your Clutter Account at
                </strong>
              </span>{' '}
              <a
                href="https://account.clutter.com"
                target="_blank"
                rel="noopener"
              >
                <span>
                  <strong>https://account.clutter.com</strong>
                </span>
              </a>
              <strong>.</strong>
            </p>
            <p>
              Upon cancellation of the Services for any reason, you must contact
              Clutter promptly to arrange for delivery of your Customer Items,
              if applicable. If within 45 days following cancellation of the
              Services for any reason you fail to arrange for delivery of all of
              your Customer Items, then Clutter may process the Customer Items
              in accordance with the provisions of the following section titled
              &ldquo;Clutter&rsquo;s Right to Withhold or Dispose of Customer
              Items&rdquo;.
            </p>
            <p>
              If you commit to a minimum payment term for any Services, you
              remain obligated to pay the full amount of any such minimum
              payment term you have made to Clutter, regardless of whether and
              at what point you cancel your Clutter Account.
            </p>
          </li>
          <li>
            <div>
              Clutter&rsquo;s Right to Withhold or Dispose of Customer Items
            </div>
            <p>
              Subject to applicable law, Clutter shall have the right to
              withhold and/or dispose of some or all of the Customer Items in
              accordance with this clause if either: (i) you do not pay any
              applicable fees or any other payments due under these Terms, or
              (ii) you abandon your Customer Items.
            </p>
            <p>
              You will be responsible for all transport and storage charges and
              other associated costs reasonably incurred by Clutter while
              withholding or disposing of the Customer Items. Clutter will
              provide you with 45 days&rsquo; written notice requiring you to
              pay all amounts due and contact Clutter to arrange for re-delivery
              of the Customer Items. If upon the expiration of the 45-day notice
              period you have failed to pay all of the amounts due, Clutter will
              consider your Customer Items abandoned and may dispose of some or
              all of the Customer Items by sale or otherwise.
            </p>
            <p>
              If in Clutter&rsquo;s opinion the Customer Items cannot be sold
              for a reasonable price or at all (for any reason), or despite
              Clutter&rsquo;s reasonable efforts they remain unsold, you
              authorize Clutter to treat them as abandoned and to destroy or
              otherwise dispose of them at your cost. You shall be responsible
              for all costs reasonably incurred by Clutter in relation to the
              disposal of the Customer Items. If Clutter receives money on
              disposal of the Customer Items, the net proceeds of sale will be
              credited to your account and Clutter will pay any excess amounts
              to you without interest, less Clutter&rsquo;s administrative
              charge of $300.00.
            </p>
            <p>
              If, after having made reasonable efforts to do so, Clutter is
              unable to return any excess amounts received by Clutter from the
              disposal of your Customer Items to you, including having given not
              less than 90 days&rsquo; written notice to you, Clutter may retain
              any such excess amounts for its own account. If the proceeds of
              sale (if any) are insufficient to discharge the outstanding
              charges or any other payments due to Clutter under these Terms and
              the costs of sale, you must pay any balance outstanding to Clutter
              within 7 days of a written demand from Clutter. Interest will
              accrue on the balance in accordance with the Payment Section above
              until the balance is paid in full.
            </p>
          </li>
          <li>
            <div>Clutter&rsquo;s Limited Security Warranty</div>
            <p>
              Clutter agrees to provide you with a limited security warranty
              regarding your Customer Items as set forth in the terms and
              conditions of our{' '}
              <a
                href="https://www.clutter.com/legal/limited_security_warranty"
                target="_blank"
                rel="noopener"
              >
                <span>Limited Security Warranty Policy</span>
              </a>
              . This{' '}
              <a
                href="https://www.clutter.com/legal/limited_security_warranty"
                target="_blank"
                rel="noopener"
              >
                Limited Security Warranty Policy
              </a>{' '}
              is expressly incorporated by reference herein. The Customer Items
              are not insured by Clutter against fire or any other casualty, and
              Clutter does not sell insurance for any risk or purpose.
            </p>
          </li>
          <li>
            <div>Termination</div>
            <p>
              You may terminate these Terms at any time by: (1) canceling the
              Services pursuant to Section 2.E; (2) <strong>and</strong> paying
              any outstanding amounts owed to Clutter.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <div>
          <strong>OUR PROPRIETARY RIGHTS</strong>
        </div>
        <p>
          Except for the content you submit, post, display, or otherwise make
          available to Clutter in connection with the Services (&ldquo;
          <strong>User Content</strong>&rdquo;), the Site and all materials
          therein or transferred thereby, including, without limitation,
          software, images, text, graphics, illustrations, logos, patents,
          trademarks, Site marks, copyrights, photographs, audio, videos, music,
          and content belonging to other Users (the &ldquo;
          <strong>Clutter Content</strong>
          &rdquo;), and all intellectual property rights related thereto, are
          the exclusive property of Clutter and its licensors (including other
          Users who post User Content to the Site). Except as explicitly
          provided herein, nothing in these Terms shall be deemed to create a
          license in or under any such intellectual property rights, and you
          agree not to sell, license, rent, modify, distribute, copy, reproduce,
          transmit, publicly display, publicly perform, publish, adapt, edit or
          create derivative works from any Clutter Content. Use of the Clutter
          Content for any purpose not expressly permitted by these Terms is
          strictly prohibited.{' '}
          <span style={{ textDecoration: 'underline' }}>
            <strong>
              You hereby expressly grant, and you represent and warrant that you
              have all rights necessary to grant, to Clutter a royalty-free,
              sublicensable, transferable, perpetual, irrevocable,
              non-exclusive, worldwide right and license to use, reproduce,
              modify, publicly perform, display, and make derivative works of
              your User Content, in whole or in part, and in any form, media or
              technology, whether now known or hereafter developed, for use in
              connection with the Services.
            </strong>
          </span>
        </p>
        <p>
          You may choose to or we may invite you to submit feedback, comments,
          suggestions, error identifications, or ideas about the Site or
          Services, including without limitation about how to improve the Site,
          the Services, or our other services or products (&ldquo;
          <strong>Idea(s)</strong>&rdquo;). By submitting any Idea, you agree
          that your disclosure is gratuitous, unsolicited, and without
          restriction and will not place Clutter under any fiduciary or other
          obligation, and that we are free to use the Idea without any
          additional compensation to you, and/or to disclose the Idea on a
          non-confidential basis or otherwise to anyone. You further acknowledge
          that, by acceptance of your submission, Clutter does not waive any
          rights to use similar or related ideas previously known to Clutter, or
          developed by its employees, or obtained from sources other than you.
        </p>
      </li>
      <li>
        <div>
          <strong>SMS/TEXT MESSAGE ALERTS AND CALLS</strong>
        </div>
        <p>
          By consenting to Clutter&rsquo;s SMS/text messaging and calls service,
          you agree to receive SMS/text messages and calls with service,
          transaction, account-related, and commercial news, events, offers, and
          promotions from and on behalf of Clutter to your mobile phone number
          provided, even if your mobile number is registered on any state or
          federal Do Not Call list. You certify that your mobile number provided
          is true and accurate and that you are authorized to enroll the
          designated mobile number to receive such text messages and calls. You
          are strictly prohibited from registering a mobile number that is not
          your own. You acknowledge and agree that calls or text messages may be
          sent using automated technology, including through an automatic
          telephone dialing system.
        </p>
        <p>
          You understand that you do not have to sign up for this program in
          order to make any purchases, and your consent is not a condition of
          any purchase with Clutter. Your participation in our alerts program is
          completely voluntary. Standard message and data rates may apply from
          your carrier. Check your mobile plan and contact your mobile carrier
          for details. You are solely responsible for all charges related to
          SMS/text messages and calls, including charges from your mobile
          carrier. You can unsubscribe at any time.
        </p>
        <p>
          You may opt-out of receiving promotional calls or SMS text messages at
          any time. Please note that unsubscribing from promotional calls and
          SMS text messages will not prevent you from receiving calls or SMS
          texts from Clutter directly relating to your use of the Service, such
          as account information, item pick-up details and delivery information
          that are necessary to provide the requested Service to you. To
          unsubscribe from promotional calls or SMS texts, reply to one of our
          text messages with the word &ldquo;STOP&rdquo;, submit a ticket in
          your Clutter Account, or email optout@clutter.com and provide your
          phone number and opt-out preference.
        </p>
      </li>
      <li>
        <div>
          <strong>NO SALES TO CHILDREN</strong>
        </div>
        <p>
          Clutter does not sell products through its Site or Service for or to
          children, but only to adults, who can purchase with a credit card or
          other permitted payment method. If you are under eighteen (18) years
          of age, you may use the Service only with involvement and consent of a
          parent or legal guardian.
        </p>
      </li>
      <li>
        <div>
          <strong>CALIFORNIA RESIDENTS</strong>
        </div>
        <p>
          The provider of services is set forth herein. If you are a California
          resident, in accordance with Cal. Civ. Code &sect;1789.3, you may
          report complaints to the Complaint Assistance Unit of the Division of
          Consumer Services of the California Department of Consumer Affairs by
          contacting them in writing at 1625 North Market Blvd., Suite N 112
          Sacramento, CA 95834, or by telephone at (800) 952-5210 or (916)
          445-1254.
        </p>
      </li>
      <li>
        <div>
          <strong>PRIVACY</strong>
        </div>
        <p>
          We care about the privacy of our Users. You understand that by using
          the Site you consent to the collection, use, and disclosure of your
          personally identifiable information and aggregate data as set forth in
          our{' '}
          <a
            href="https://www.clutter.com/legal/privacy_policy"
            target="_blank"
            rel="noopener"
          >
            Privacy Policy
          </a>
          , and to have your personally identifiable information collected,
          used, transferred to, and processed in the United States.
        </p>
      </li>
      <li>
        <div>
          <strong>SECURITY</strong>
        </div>
        <p>
          Clutter cares about the integrity and security of your personal
          information. However, we cannot guarantee that unauthorized third
          parties will never be able to defeat our security measures or use your
          personal information for improper purposes. You acknowledge that you
          provide your personal information at your own risk.
        </p>
      </li>
      <li>
        <div>
          <strong>THIRD-PARTY LINKS</strong>
        </div>
        <p>
          The Site may contain links to third-party websites, advertisers,
          services, special offers, or other events or activities that are not
          owned or controlled by Clutter. Clutter does not endorse or assume any
          responsibility for any such third-party sites, information, materials,
          products, or services. If you access a third party website from the
          Site, you do so at your own risk, and you understand that these Terms
          and Clutter&rsquo;s{' '}
          <a
            href="https://www.clutter.com/legal/privacy_policy"
            target="_blank"
            rel="noopener"
          >
            <span>Privacy Policy</span>
          </a>{' '}
          do not apply to your use of such sites and services. You expressly
          relieve Clutter from any and all liability arising from your use of
          any third-party website, service, or content. Additionally, your
          dealings with or participation in promotions of advertisers found on
          the Site or in connection with the Services, including payment and
          delivery of goods, and any other terms (such as warranties) are solely
          between you and such advertisers. You agree that Clutter shall not be
          responsible for any loss or damage of any sort relating to your
          dealings with such advertisers.
        </p>
      </li>
      <li>
        <div>
          <strong>INDEMNITY</strong>
        </div>
        <p>
          You agree to defend, indemnify and hold harmless Clutter and its
          subsidiaries, agents, licensors, managers, and other affiliated
          companies, and their employees, contractors, agents, officers and
          directors, from and against any and all claims, damages, obligations,
          losses, liabilities, costs or debt, and expenses (including but not
          limited to attorney&rsquo;s fees) arising from: (i) your use of and
          access to the Site, including any data or content transmitted or
          received by you; (ii) your use of the Services; (iii) your violation
          of any term of these Terms, including without limitation your breach
          of any of the representations and warranties above; (iv) your
          violation of any third-party right, including without limitation any
          right of privacy or intellectual property; (v) your violation of any
          applicable law, rule, or regulation; (vi) any of your User Content or
          Customer Items, or any that is submitted via your account; or (vii)
          any other party&rsquo;s access and use of the Site or Services with
          your unique username, password or other appropriate security code.
        </p>
      </li>
      <li>
        <div>
          <strong>NO WARRANTY</strong>
        </div>
        <p>
          WE STRIVE TO PROVIDE THE SERVICES IN THE WAY YOU EXPECT THEM, BUT
          THERE ARE SOME THINGS IT IS IMPORTANT FOR YOU TO UNDERSTAND THAT WE
          CANNOT PROMISE.
        </p>
        <p>
          EXCEPT AS EXPRESSLY PROVIDED IN THE{' '}
          <a
            href="https://www.clutter.com/legal/limited_security_warranty"
            target="_blank"
            rel="noopener"
          >
            LIMITED SECURITY WARRANTY POLICY
          </a>
          , THE SITE AND SERVICES ARE PROVIDED ON AN &ldquo;AS IS&rdquo; AND
          &ldquo;AS AVAILABLE&rdquo; BASIS. USE OF THE SITE AND SERVICES IS AT
          YOUR OWN RISK. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE
          SITE AND SERVICES ARE PROVIDED WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES
          OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
          NON-INFRINGEMENT. NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN,
          OBTAINED BY YOU FROM CLUTTER OR THROUGH THE SITE OR IN CONNECTION WITH
          THE SERVICES WILL CREATE ANY WARRANTY NOT EXPRESSLY STATED HEREIN.
          WITHOUT LIMITING THE FOREGOING, CLUTTER, ITS SUBSIDIARIES, ITS
          AFFILIATES, AND ITS LICENSORS DO NOT WARRANT THAT THE CONTENT IS
          ACCURATE, RELIABLE OR CORRECT; THAT THE SITE OR SERVICES WILL MEET
          YOUR REQUIREMENTS; THAT THE SITE OR SERVICES WILL BE AVAILABLE AT ANY
          PARTICULAR TIME OR LOCATION, UNINTERRUPTED, OR SECURE; THAT ANY
          DEFECTS OR ERRORS WILL BE CORRECTED; OR THAT THE SITE IS FREE OF
          VIRUSES OR OTHER HARMFUL COMPONENTS. ANY CONTENT DOWNLOADED OR
          OTHERWISE OBTAINED THROUGH THE USE OF THE SITE IS DOWNLOADED AT YOUR
          OWN RISK AND YOU WILL BE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR
          COMPUTER SYSTEM OR MOBILE DEVICE OR LOSS OF DATA THAT RESULTS FROM
          SUCH DOWNLOAD OR YOUR USE OF THE SITE.
        </p>
        <p>
          CLUTTER DOES NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY
          FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED BY A THIRD PARTY
          THROUGH THE CLUTTER SITE OR SERVICES OR ANY WEBSITE OR SERVICE
          HYPERLINKED TO THE SITE, AND CLUTTER WILL NOT BE A PARTY TO OR IN ANY
          WAY MONITOR ANY TRANSACTION BETWEEN YOU AND THIRD-PARTY PROVIDERS OF
          PRODUCTS OR SERVICES.
        </p>
        <p>
          FEDERAL LAW, SOME STATES, PROVINCES, AND OTHER JURISDICTIONS DO NOT
          ALLOW EXCLUSIONS AND LIMITATIONS OF CERTAIN IMPLIED WARRANTIES, SO
          SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU. THESE TERMS GIVE
          YOU SPECIFIC LEGAL RIGHTS, AND YOU MAY ALSO HAVE OTHER RIGHTS WHICH
          VARY FROM STATE TO STATE. THE DISCLAIMERS AND EXCLUSIONS UNDER THESE
          TERMS WILL NOT APPLY TO THE EXTENT PROHIBITED BY APPLICABLE LAW.
        </p>
      </li>
      <li>
        <div>
          <strong>LIMITATION OF LIABILITY</strong>
        </div>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT, AND
          UNDER NO LEGAL THEORY, SHALL CLUTTER, ITS AFFILIATES, AGENTS,
          DIRECTORS, EMPLOYEES, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY
          INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY
          DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS,
          GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES THAT RESULT FROM THE
          USE OF, OR INABILITY TO USE, THIS SITE OR SERVICES. UNDER NO
          CIRCUMSTANCES WILL CLUTTER BE RESPONSIBLE FOR ANY DAMAGE, LOSS, OR
          INJURY RESULTING FROM HACKING, TAMPERING, OR OTHER UNAUTHORIZED ACCESS
          OR USE OF THE SITE OR YOUR CLUTTER ACCOUNT OR THE INFORMATION
          CONTAINED THEREIN.
        </p>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, CLUTTER ASSUMES NO
          LIABILITY OR RESPONSIBILITY FOR ANY: (I) ERRORS, MISTAKES, OR
          INACCURACIES OF CONTENT; (II) PERSONAL INJURY OR PROPERTY DAMAGE, OF
          ANY NATURE WHATSOEVER, RESULTING FROM YOUR ACCESS TO OR USE OF OUR
          SITE OR SERVICES (EXCEPT AS EXPRESSLY PROVIDED IN THE{' '}
          <a
            href="https://www.clutter.com/legal/limited_security_warranty"
            target="_blank"
            rel="noopener"
          >
            LIMITED SECURITY WARRANTY POLICY
          </a>
          ); (III) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS
          AND/OR ANY AND ALL PERSONAL INFORMATION STORED THEREIN; (IV) ANY
          INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SITE; (V) ANY
          BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE THAT MAY BE TRANSMITTED TO
          OR THROUGH OUR SITE BY ANY THIRD PARTY; (VI) ANY ERRORS OR OMISSIONS
          IN ANY CONTENT OR FOR ANY LOSS OR DAMAGE INCURRED AS A RESULT OF THE
          USE OF ANY CONTENT POSTED, EMAILED, TRANSMITTED, OR OTHERWISE MADE
          AVAILABLE THROUGH THE SITE; AND/OR (VII) USER CONTENT OR THE
          DEFAMATORY, OFFENSIVE, OR ILLEGAL CONDUCT OF ANY THIRD PARTY. EXCEPT
          AS EXPRESSLY PROVIDED IN THE{' '}
          <a
            href="https://www.clutter.com/legal/limited_security_warranty"
            target="_blank"
            rel="noopener"
          >
            LIMITED SECURITY WARRANTY POLICY
          </a>
          , IN NO EVENT SHALL CLUTTER, ITS AFFILIATES, AGENTS, DIRECTORS,
          EMPLOYEES, SUPPLIERS, OR LICENSORS BE LIABLE TO YOU FOR ANY CLAIMS,
          PROCEEDINGS, LIABILITIES, OBLIGATIONS, DAMAGES, LOSSES, OR COSTS IN AN
          AMOUNT EXCEEDING THE AMOUNT YOU PAID TO CLUTTER HEREUNDER IN THE
          TWELVE (12) MONTHS PRIOR TO THE DATE THE LAST CAUSE OF ACTION AROSE OR
          $100.00, WHICHEVER IS GREATER.
        </p>
        <p>
          BECAUSE OF THE NATURE OF CLUTTER&rsquo;S BUSINESS AND THE NATURE OF
          HOUSEHOLD GOODS, THERE ARE CERTAIN EVENTS AND CIRCUMSTANCES WHICH MAY
          RESULT IN LOSS OR DAMAGE TO CUSTOMER ITEMS FOR WHICH CLUTTER WILL NOT
          BE LIABLE. THESE PERILS INCLUDE LOSS OR DAMAGE RESULTING FROM ACT OR
          DEFAULT OF YOU, EARTHQUAKE, FIRE, MOTHS, INSECTS, RODENTS, WEATHER,
          MOLD, MILDEW, RUST, LEAKAGE, MOISTURE OR WATER, CHANGES IN
          TEMPERATURE, HEAT, FUMIGATION, INHERENT VICE, NORMAL WEAR AND TEAR,
          ACTS OF GOD, LABOR TROUBLES OR STRIKES, WAR OR INSURRECTION, RIOT OR
          CIVIL COMMOTIONS, ACTS OF PUBLIC ENEMY, STREET TRAFFIC, ELEVATOR
          SERVICE, OR DETERIORATION BY TIME, FURNITURE ASSEMBLED BY YOU COMMONLY
          REFERRED TO AS READY TO ASSEMBLE OR KNOCKED DOWN (KD) FURNITURE; NOR
          FOR LOSS OR DAMAGE TO FRAGILE ARTICLES UNLESS PACKED BY CLUTTER, AND
          UNPACKED BY THEM AT THE TIME OF DELIVERY; NOR FOR THE CONTENTS OF ANY
          CONTAINER OR ARTICLE OF FURNITURE UNLESS SUCH CONTENTS ARE MADE KNOWN
          AND SPECIFICALLY ITEMIZED IN AN INVENTORY; NOR FOR OTHER CAUSES BEYOND
          ITS CONTROL.
        </p>
        <p>
          CLUTTER ALSO WILL NOT BE LIABLE FOR LOSS OR DAMAGE OCCURRING OR
          ALLEGED TO HAVE OCCURRED TO THE MECHANICAL FUNCTION OF ITEMS INCLUDED
          IN THE CUSTOMER ITEMS SUCH AS, BUT NOT LIMITED TO, MUSICAL
          INSTRUMENTS, RADIOS, STEREO SETS, TELEVISION SETS, COMPUTERS,
          REFRIGERATORS, AIR CONDITIONERS, OR OTHER ELECTRONICS, INSTRUMENTS, OR
          APPLIANCES.
        </p>
        <p>
          UNDER NO CIRCUMSTANCES WILL CLUTTER BE LIABLE FOR ANY SPECIAL,
          INCIDENTAL, INDIRECT OR CONSEQUENTIAL DAMAGES OF ANY KIND, INCLUDING
          BUT NOT LIMITED TO LOST INCOME, LOST PROFITS OR MARKET, LOST
          OPPORTUNITY, LOSS OF BUSINESS, ADDED LABOR CAUSED BY DELAY OR
          &ldquo;DOWN TIME,&rdquo; LATE PENALTIES, ANTIQUE OR SENTIMENTAL VALUE,
          ATTORNEY&rsquo;S FEES, COSTS OF DEFENSE OR ANY OTHER FORMS OF DAMAGE,
          WHETHER DIRECT, INCIDENTAL, INDIRECT, CONSEQUENTIAL, OR SPECIAL,
          REGARDLESS OF WHETHER CLUTTER HAD KNOWLEDGE THAT SUCH DAMAGE(S) MIGHT
          BE INCURRED REGARDLESS OF WHETHER SUCH DAMAGES ARE ALLEGED TO HAVE
          RESULTED FROM NEGLIGENCE, GROSS NEGLIGENCE, INTENTIONAL, OR WILFUL
          MISCONDUCT.
        </p>
        <p>
          With respect to the loss of or damage to Customer Items during
          transportation, Clutter&rsquo;s liability as a motor carrier shall be
          determined in accordance with Article 3 of the Uniform Commercial
          Code, subject to the limitations set forth in these Terms and any
          applicable Bill of Lading, Estimate, Warehouse Receipt, Tariff, or
          Storage Contract.
        </p>
        <p>
          With respect to the loss of or damage to Customer Items during
          storage, Clutter&rsquo;s liability as a warehouseman shall be
          determined in accordance with Article 7 of the Uniform Commercial
          Code, subject to the limitations set forth in these Terms, any
          applicable Bill of Lading, Estimate, Warehouse Receipt, Tariff, or
          Storage Contract.
        </p>
        <p>
          THIS LIMITATION OF LIABILITY SECTION APPLIES WHETHER THE ALLEGED
          LIABILITY IS BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR
          ANY OTHER BASIS, EVEN IF CLUTTER HAS BEEN ADVISED OF THE POSSIBILITY
          OF SUCH DAMAGE. THE FOREGOING LIMITATION OF LIABILITY SHALL APPLY TO
          THE FULLEST EXTENT PERMITTED BY LAW IN THE APPLICABLE JURISDICTION.
        </p>
        <p>
          SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL OR
          CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATIONS OR EXCLUSIONS MAY NOT
          APPLY TO YOU. THESE TERMS GIVE YOU SPECIFIC LEGAL RIGHTS, AND YOU MAY
          ALSO HAVE OTHER RIGHTS WHICH VARY FROM STATE TO STATE. THE
          DISCLAIMERS, EXCLUSIONS, AND LIMITATIONS OF LIABILITY UNDER THESE
          TERMS WILL NOT APPLY TO THE EXTENT PROHIBITED BY APPLICABLE LAW.
        </p>
        <p>
          The Site is controlled and operated from facilities in the United
          States. Clutter makes no representations that the Site is appropriate
          or available for use in other locations. Those who access or use the
          Site from other jurisdictions do so at their own volition and are
          entirely responsible for compliance with all applicable United States
          and local laws and regulations, including but not limited to export
          and import regulations. You may not use the Site if you are a resident
          of a country embargoed by the United States, or are a foreign person
          or entity blocked or denied by the United States government. Unless
          otherwise explicitly stated, all materials found on the Site are
          solely directed to individuals, companies, or other entities located
          in the United States.
        </p>
      </li>
      <li>
        <div>
          <strong>WAREHOUSEMEN&rsquo;S AND CARRIER&rsquo;S LIENS</strong>
        </div>
        <p>
          You agree that Clutter claims a general lien on the Customer Items
          covered by these Terms (and on proceeds from sale of Customer Items)
          for all charges for transportation, storage, handling, and
          preservation of Customer Items and for all lawful claims for money
          advanced, interest, insurance, transportation, labor, weighing,
          coopering, and other charges and expenses in relation to the Customer
          Items, present and future and expenses necessary for preservation of
          Customer Items or incurred in their sale, as well as for like charges
          or expenses in relation to any other goods whenever deposited with
          Clutter by you. You hereby authorize Clutter to sell your Customer
          Items in accordance with law to enforce the warehouseman&rsquo;s or
          carrier&rsquo;s liens that Clutter claims hereby for storage,
          transportation, including demurrage and terminal charges, warehouse
          handling, insurance, labor, claims for money advanced, interest and
          charges present or future in relation to Customer Items, and expenses
          necessary for preservation of Customer Items or incurred in their
          sale; and subsequent to Clutter&rsquo;s termination of your Clutter
          Account where Customer Items are not removed and/or charges remain
          unpaid prior to such removal. You hereby grant a security interest to
          Clutter in the goods covered hereby to secure obligations evidenced by
          a promissory note or notes executed pursuant to this security
          agreement, and authorizes Company to file financing statements signed
          only by Clutter to perfect such interest.
        </p>
      </li>
      <li>
        <div>
          <strong>BUSINESS FAILURE</strong>
        </div>
        <p>
          In the event that Clutter ceases operations, we will attempt to make
          reasonable efforts to return or deliver your Customer Items to you,
          but cannot guarantee if and when such returns will occur. Clutter will
          not be liable for any failure to return Customer Items in the event of
          a business failure.
        </p>
      </li>
      <li>
        <div>
          <strong>YOUR ACCEPTANCE OF THESE TERMS</strong>
        </div>
        <p>
          You certify that you have read and expressly agree to these Terms and
          you acknowledge that certain provisions of the Terms limit your legal
          rights and remedies. You intend your assent to these Terms to be a
          complete and unconditional release of all liability to the greatest
          extent permitted by law.
        </p>
        <p>
          You expressly agree and acknowledge that you may discover facts or law
          different from, or in addition to, the facts or law that you know or
          believe to be true with respect to the Claims. Nonetheless, you
          expressly agree and acknowledge that this section shall be and remain
          effective in all respects notwithstanding such different or additional
          facts or the discovery of them. You expressly agree and acknowledge
          that all rights under California Civil Code Section 1542 are expressly
          waived. California Civil Code Section 1542 provides:
        </p>
        <p>
          A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH THE CREDITOR DOES
          NOT KNOW OR SUSPECT TO EXIST IN HIS OR HER FAVOR AT THE TIME OF
          EXECUTING THE RELEASE, WHICH IF KNOWN BY HIM OR HER MUST HAVE
          MATERIALLY AFFECTED HIS OR HER SETTLEMENT WITH THE DEBTOR.
        </p>
      </li>
      <li>
        <div>
          <strong>
            GOVERNING LAW, DISPUTE RESOLUTION, AND CLASS ACTION/JURY TRIAL
            WAIVER
          </strong>
        </div>
        <ol>
          <li>
            <div>Governing Law</div>
            <p>
              These Terms shall be governed by the internal substantive laws of
              the State of California, without respect to its conflict of laws
              principles. The parties acknowledge that these Terms evidence a
              transaction involving interstate commerce. Notwithstanding the
              preceding sentences with respect to the substantive law, any
              arbitration conducted pursuant to these Terms shall be governed by
              the Federal Arbitration Act (9 U.S.C. &sect;&sect; 1-16). The
              application of the United Nations Convention on Contracts for the
              International Sale of Customer Items is expressly excluded. You
              agree to submit to the personal jurisdiction of the federal and
              state courts located in Los Angeles, California for any suits,
              actions, or proceedings for which we retain the right to seek
              injunctive or other equitable relief in a court of competent
              jurisdiction to prevent the actual or threatened infringement,
              misappropriation or violation of our copyrights, trademarks, trade
              secrets, patents, or other intellectual property or proprietary
              rights, as set forth in the Dispute Resolution provision below.
            </p>
          </li>
          <li>
            <div>Arbitration</div>
            <p>
              In the event a dispute arises between the parties to these Terms,
              you hereby agree to first attempt to resolve the dispute
              informally via negotiation. If the dispute has not been resolved
              after thirty (30) days, the parties agree to resolve any claim,
              dispute, or controversy (excluding any claims for injunctive or
              other equitable relief as provided below) arising out of or in
              connection with or relation to these Terms, or the breach or
              alleged breach, (i) by binding arbitration by FairClaims, Inc. or
              another arbitration provider selected by Clutter; or (ii) by
              filing a claim in court. You hereby waive, to the fullest extent
              permitted by applicable law, any objection that it may now or
              thereafter have to the laying of the venue of any such suit,
              action or proceeding in any such court or that any such suit,
              action or proceeding brought in any such court has been brought in
              an inconvenient forum. Process in any such suit, action or
              proceeding may be served on any party anywhere in the world,
              whether within or without the jurisdiction of any such court.
            </p>
          </li>
          <li>
            <div>Class Action/Jury Trial Waiver</div>
            <p>
              WITH RESPECT TO ALL PERSONS AND ENTITIES, REGARDLESS OF WHETHER
              THEY HAVE OBTAINED THE SITE OR STORAGE SERVICES FOR PERSONAL,
              COMMERCIAL, OR OTHER PURPOSES, ALL CLAIMS MUST BE BROUGHT IN THE
              PARTIES&rsquo; INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR
              CLASS MEMBER IN ANY PURPORTED CLASS ACTION, COLLECTIVE ACTION,
              PRIVATE ATTORNEY GENERAL ACTION, OR OTHER REPRESENTATIVE
              PROCEEDING. THIS WAIVER APPLIES TO CLASS ARBITRATION, AND, UNLESS
              WE AGREE OTHERWISE, THE ARBITRATOR MAY NOT CONSOLIDATE MORE THAN
              ONE PERSON&rsquo;S CLAIMS. YOU AND CLUTTER AGREE THAT THE
              ARBITRATOR MAY AWARD RELIEF ONLY TO AN INDIVIDUAL CLAIMANT AND
              ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF ON YOUR INDIVIDUAL
              CLAIM(S). ANY RELIEF AWARDED MAY NOT AFFECT OTHER CLUTTER USERS.
              YOU AND CLUTTER AGREE THAT, BY ENTERING INTO THESE TERMS, YOU AND
              CLUTTER ARE EACH WAIVING THE RIGHT TO A TRIAL BY JURY OR TO BRING,
              JOIN, OR PARTICIPATE IN A CLASS ACTION, COLLECTIVE ACTION, PRIVATE
              ATTORNEY GENERAL ACTION, OR OTHER REPRESENTATIVE PROCEEDING OF ANY
              KIND AS A PLAINTIFF OR CLASS MEMBER.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <div>
          <strong>GENERAL</strong>
        </div>
        <ol>
          <li>
            <div>Assignment</div>
            <p>
              These Terms, and any rights and licenses granted hereunder, may
              not be transferred or assigned by you, but may be assigned by
              Clutter without restriction. Any attempted transfer or assignment
              in violation hereof shall be null and void.
            </p>
          </li>
          <li>
            <div>Force Majeure</div>
            <p>
              Notwithstanding anything to the contrary, Clutter will not be
              liable or responsible for any damage to or loss of any Customer
              Items, or failure to perform, or delay in performance of, any of
              its obligations under these Terms, including without limitation
              any Services, that is caused by events outside its reasonable
              control (including but not limited to) acts of God, flood,
              earthquake, windstorm or other natural disaster; war, armed
              conflict, terrorist attack, civil war, civil commotion or riots;
              nuclear, chemical or biological contamination or sonic boom;
              pandemic; any law or government order, rule, regulation or
              direction, or any action taken by a government or public
              authority; fire, explosion or accidental damage; adverse weather
              conditions; interruption or failure of utility service, including
              but not limited to electric power, gas or water; any labor
              dispute, including but not limited to strikes, industrial action
              or lockouts; non-performance or delay by suppliers or
              subcontractors; and failure of plant machinery, machinery,
              vehicles, computers, the Internet or telecommunications (each, a
              &ldquo;
              <strong>Force Majeure Event</strong>&rdquo;).
            </p>
          </li>
          <li>
            <div>Notification Procedures and Changes to these Terms.</div>
            <p>
              Clutter may provide notifications, whether such notifications are
              required by law or are for marketing or other business related
              purposes, to you via email notice, written or hard copy notice, or
              through posting of such notice on our website or in your Clutter
              Account, as determined by Clutter in our sole discretion. Clutter
              reserves the right to determine the form and means of providing
              notifications to our Users, provided that you may opt out of
              certain means of notification as described in these Terms. Clutter
              is not responsible for any automatic filtering you or your network
              provider may apply to email notifications we send to the email
              address you provide us. Clutter may, in its sole discretion,
              modify or update these Terms from time to time, and so you should
              review this page periodically. When we change these Terms in a
              material manner, we will update the &ldquo;last modified&rdquo;
              date at the bottom of this page and notify you that material
              changes have been made to these Terms. Subject to applicable laws,
              all changes to these Terms are effective immediately when we post
              them, and apply to all access to and use of the Site or Services
              thereafter. Your continued use of the Site or Services after any
              such change constitutes your acceptance of the new Terms of Use.
              If you do not agree to any of these terms or any future Terms of
              Use, do not use or access (or continue to use or access) the Site
              or Services.
            </p>
          </li>
          <li>
            <div>Entire Agreement/Severability</div>
            <p>
              These Terms, together with the{' '}
              <a
                href="https://www.clutter.com/legal/privacy_policy"
                target="_blank"
                rel="noopener"
              >
                Privacy Policy
              </a>
              ,{' '}
              <a
                href="https://www.clutter.com/legal/limited_security_warranty"
                target="_blank"
                rel="noopener"
              >
                Limited Security Warranty Policy
              </a>
              , any amendments to any of the foregoing, and any additional
              agreements you may enter into with Clutter in connection with the
              Site or Services, shall constitute the entire agreement between
              you and Clutter concerning the Site and Services. If any provision
              of these Terms is deemed invalid by a court of competent
              jurisdiction, the invalidity of such provision shall not affect
              the validity of the remaining provisions of these Terms, which
              shall remain in full force and effect.
            </p>
          </li>
          <li>
            <div>No Waiver</div>
            <p>
              No waiver of any term of these Terms shall be deemed a further or
              continuing waiver of such term or any other term, and
              Clutter&rsquo;s failure to assert any right or provision under
              these Terms shall not constitute a waiver of such right or
              provision.
            </p>
          </li>
          <li>
            <div>Contact.</div>
            <p>
              Please contact us through your Clutter Account at
              https://account.clutter.com with any questions regarding these
              Terms.
            </p>
          </li>
        </ol>
      </li>
    </ol>
    <p>Last modified: August 31, 2023</p>
  </Content>
);
